import * as Y from 'yjs';
import { useEntityStore } from '@/lib/sync/entity-store';
import type { PendingSnapshotOp, OpResult } from '@/lib/sync/entity-store';
import {
  readSnapshotState,
  writeSnapshotState,
  deleteSnapshotState,
} from '@/lib/sync/snapshot-state-cache';
export type { SnapshotInfo } from '@/lib/sync/entity-store';

/**
 * Narrow interface describing the subset of the opSyncBridge handle that
 * SnapshotService needs — submit a one-off snapshot-op envelope and await
 * the correlated OpResult. Keeps the service decoupled from the full bridge.
 */
export interface EphemeralOpSender {
  sendEphemeralSnapshotBatch(
    op: PendingSnapshotOp,
    options?: { binaryTarget?: string; binary?: Uint8Array },
  ): Promise<OpResult[]>;
}

/** Encode binary data as base64 in chunks to avoid stack overflow on large docs. */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function makeOp(ops: PendingSnapshotOp['ops']): PendingSnapshotOp {
  return {
    opId: crypto.randomUUID(),
    ops,
    createdAt: Date.now(),
    sent: false,
  };
}

/** Decode a base64-encoded string to a Uint8Array. */
function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export class SnapshotService {
  constructor(
    private readonly getDoc?: (entityId: string) => Y.Doc | undefined,
    private readonly opSync?: EphemeralOpSender,
  ) {}

  createSnapshot(entityId: string, name?: string): string {
    // Generate the snapshot id client-side so the optimistic UI apply and
    // the eventual server-confirmed row share a stable identifier. This is
    // what makes offline snapshot creation work end-to-end: the user sees
    // the snapshot in the list immediately, it persists in pendingOps
    // through page reloads, and when the op flushes on reconnect the
    // server uses this id (not its own generated one) so no reconciliation
    // is needed.
    const snapshotId = crypto.randomUUID();

    let stateBase64: string | undefined;
    try {
      const doc = this.getDoc?.(entityId);
      if (doc) {
        // V2 format — the server encodes auto-snapshots via
        // encode_state_as_update_v2, the diff view decodes with Y.applyUpdateV2,
        // and the restore path reads raw bytes from the DB. Mixing V1 and V2
        // would mean manual snapshots can't be diffed or restored.
        const state = Y.encodeStateAsUpdateV2(doc);
        stateBase64 = uint8ToBase64(state);
      }
    } catch {
      // fall back to server state — the server will read from live room or DB
    }

    const trigger = 'manual';
    const timestamp = new Date().toISOString();

    useEntityStore.getState().addSnapshotOp(makeOp([{
      action: 'create',
      entityId: entityId,
      fields: {
        snapshotId,
        trigger,
        ...(name ? { name } : {}),
        ...(stateBase64 ? { state: stateBase64 } : {}),
      },
    }]));

    // Optimistic apply: the snapshot appears in the local store (and the
    // snapshot list UI) immediately, whether or not the WS is connected.
    // On reconnect, the server confirms under the same id and the row
    // gets overwritten with identical data. Remote tabs receive their
    // own UPSERTED via the server's broadcast.
    useEntityStore.getState().applyEvent({
      id: '',
      kind: 'UPSERTED',
      modelKind: 'snapshot',
      modelId: snapshotId,
      data: {
        id: snapshotId,
        entityId: entityId,
        trigger,
        name: name ?? null,
        timestamp,
      } as unknown as Record<string, unknown>,
    });

    return snapshotId;
  }

  renameSnapshot(_entityId: string, snapshotId: string, name: string | null): void {
    useEntityStore.getState().addSnapshotOp(makeOp([{
      action: 'update',
      entityId: snapshotId,
      fields: { name },
    }]));
  }

  restoreSnapshot(_entityId: string, snapshotId: string): void {
    useEntityStore.getState().addSnapshotOp(makeOp([{
      action: 'restore',
      entityId: snapshotId,
    }]));
  }

  deleteOneSnapshot(_entityId: string, snapshotId: string): void {
    useEntityStore.getState().addSnapshotOp(makeOp([{
      action: 'delete',
      entityId: snapshotId,
    }]));
    // Optimistic apply — the server skips broadcast-to-self on entity events,
    // so without this the originating tab's snapshot list stays stale until
    // a reload. Matches the pattern used by workspaceService for doc deletes.
    useEntityStore.getState().applyEvent({
      id: '', kind: 'DELETED', modelKind: 'snapshot', modelId: snapshotId, data: null,
    });
    // Evict any cached state bytes for this snapshot so we don't leak IDB.
    void deleteSnapshotState(snapshotId);
  }

  deleteAllSnapshots(entityId: string): void {
    // Client fan-out: enqueue N independent delete ops, one per snapshot
    // currently in the store for this doc. Each op gets its own opId, is
    // correlated independently by the server's op_log dedup, and the server
    // broadcasts a DELETED entity event per snapshot for other tabs. Realistic
    // snapshot counts (10-50 per doc) make this trivial traffic-wise.
    //
    // A server-side { all: true } bulk op was considered and rejected: it
    // would require changing process_single_op to return multiple entity
    // events (17+ call sites), with no measurable benefit at Nula's scale.
    const toDelete: string[] = [];
    for (const snap of useEntityStore.getState().snapshots.values()) {
      if (snap.entityId === entityId) toDelete.push(snap.id);
    }

    const store = useEntityStore.getState();
    for (const snapshotId of toDelete) {
      store.addSnapshotOp(makeOp([{
        action: 'delete',
        entityId: snapshotId,
      }]));
      // Optimistic — originating tab never sees its own broadcast.
      store.applyEvent({
        id: '', kind: 'DELETED', modelKind: 'snapshot', modelId: snapshotId, data: null,
      });
      // Evict cached state bytes for this snapshot.
      void deleteSnapshotState(snapshotId);
    }
  }

  /**
   * Load a snapshot's state bytes.
   *
   * Three-tier resolution:
   *   1. pendingOps — if the snapshot is still waiting to flush (offline
   *      create or just-created and not yet confirmed), the bytes live
   *      locally in the op's fields.state as base64. Fully offline.
   *   2. IndexedDB cache (snapshotStateCache) — bytes from a previously
   *      successful WS fetch, kept in IDB so a historical snapshot can be
   *      reopened offline after it's been viewed at least once online.
   *      Snapshots are immutable, so cached bytes never go stale.
   *   3. WS previewOnly restore op — server round-trip, last resort.
   *      On success, the bytes are written through to tier 2.
   */
  async fetchSnapshotState(_entityId: string, snapshotId: string): Promise<Uint8Array> {
    // Tier 1: pendingSnapshotOps — fully offline path for not-yet-flushed snapshots.
    const pendingBytes = this.findPendingSnapshotState(snapshotId);
    if (pendingBytes) return pendingBytes;

    // Tier 2: IDB cache of previously-fetched flushed snapshots.
    const cached = await readSnapshotState(snapshotId);
    if (cached) return cached;

    // Tier 3: WS round-trip.
    if (!this.opSync) {
      throw new Error('SnapshotService: opSync not wired — cannot fetch snapshot state');
    }
    const op: PendingSnapshotOp = makeOp([{
      action: 'restore',
      entityId: snapshotId,
      fields: { previewOnly: true },
    }]);

    const results = await this.opSync.sendEphemeralSnapshotBatch(op);
    const entry = results[0];
    if (!entry) {
      throw new Error(`snapshot ${snapshotId}: empty OpResult`);
    }
    if (entry.status !== 'ok') {
      throw new Error(`snapshot ${snapshotId}: ${entry.message ?? entry.status}`);
    }
    const stateB64 = entry.data?.state as string | undefined;
    if (typeof stateB64 !== 'string' || stateB64.length === 0) {
      throw new Error(`snapshot ${snapshotId}: OpResult missing data.state`);
    }
    const bytes = base64ToUint8(stateB64);

    // Write-through to tier 2 so the next fetch (offline or online) is
    // served locally. Fire-and-forget: a cache failure must not fail the
    // user-facing load.
    void writeSnapshotState(snapshotId, bytes);

    return bytes;
  }

  /**
   * Scan pendingOps for a snapshot create op with the given snapshotId and
   * return its decoded state bytes if present. Returns null if no matching
   * pending create exists (the snapshot has already flushed, never existed,
   * or was created without client-supplied state).
   */
  private findPendingSnapshotState(snapshotId: string): Uint8Array | null {
    const { pendingSnapshotOps } = useEntityStore.getState();
    for (const pending of pendingSnapshotOps) {
      for (const op of pending.ops) {
        if (op.action !== 'create') continue;
        const fields = op.fields as { snapshotId?: string; state?: string } | undefined;
        if (fields?.snapshotId !== snapshotId) continue;
        if (typeof fields.state !== 'string' || fields.state.length === 0) return null;
        return base64ToUint8(fields.state);
      }
    }
    return null;
  }
}
